import { useCallback, useEffect, useRef, useState } from 'react';
import { api, syncOutbox } from './api.js';
import { subscribeOutbox } from './outbox.js';

/**
 * Loads data from the API and re-runs when `params` change.
 * Returns the payload plus a `reload` for after a mutation.
 */
export function useApi(path, params, { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: enabled });
  const paramsKey = JSON.stringify(params ?? null);
  const requestId = useRef(0);

  const reload = useCallback(async () => {
    if (!enabled || !path) return;
    const id = ++requestId.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await api.get(path, params ? JSON.parse(paramsKey) : undefined);
      if (id === requestId.current) setState({ data, error: null, loading: false });
    } catch (error) {
      if (id === requestId.current) setState({ data: null, error, loading: false });
    }
    // params is captured through paramsKey so the callback is stable per query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, paramsKey, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { ...state, reload, setData: (data) => setState((s) => ({ ...s, data })) };
}

/** Online/offline plus the number of submissions still waiting to be sent. */
export function useConnection() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => subscribeOutbox((items) => setPending(items.length)), []);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      return await syncOutbox();
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      sync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    if (navigator.onLine) sync();
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [sync]);

  return { online, pending, syncing, sync };
}

/** Debounces a value - used for the search boxes so every keystroke is not a request. */
export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
